import { pageTitle } from '@palaccount/config';
import {
  CHARGE_CATEGORIES,
  DOCUMENT_SLOTS,
  IMPORT_STAGES,
  type AllocMethod,
  type ImportOrderResponse,
} from '@palaccount/contracts';
import type { TFunction } from 'i18next';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { Typeahead } from '@/components/ui/typeahead';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentHeaderControls } from '@/components/document-header-controls';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { importOrderDocument } from '@/features/documents/record-documents';
import { downloadXlsx } from '@/features/reports/export';
import { useItemsHeld } from '@/features/inventory/api';
import {
  blockKey,
  captureBlocks,
  trackingPayload,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';
import { formatQty } from '@/lib/format';
import { useCan } from '@/lib/permissions';
import { displayRate, inverseRate } from '@/lib/money';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import { useBillPicker } from './api';
import {
  canCancel,
  canGoBack,
  deltaQty,
  isEditable,
  nextStage,
  outstandingUnits,
  overReceived,
  previousStage,
  receiptState,
  receivingOpen,
  stageIndex,
} from './import-stages';
import {
  useAdvanceStage,
  useCancelImport,
  useImportOrder,
  useReceiveShipment,
  useSetCharges,
  useSetLandedTotal,
  useUpdateImportLine,
  useUpdateImportOrder,
} from './imports-api';
import { PurchasesTabs } from './purchases-tabs';
import { StageChip } from './stage-chip';

/**
 * PUR-06 §5 — one shipment.
 *
 * The screen is built around a single claim: the landed unit cost shown in the
 * lines table is the number stock will actually be valued at. Nothing on this
 * page computes it. Every figure below comes from the server's response, which
 * recomputes the allocation from the charge rows on each read (§15), so there
 * is no arrangement of clicks that leaves the screen showing a cost the
 * inventory does not have.
 */
export function ImportOrderPage() {
  const { t } = useTranslation();
  const { importId } = useParams<{ importId: string }>();
  const { data: order, isPending, isError } = useImportOrder(importId);

  useEffect(() => {
    document.title = pageTitle(order ? order.ref : t('imports.title'));
  }, [order, t]);

  if (isPending) return <p className="p-8 text-muted-foreground">{t('common.loading')}</p>;
  if (isError || !order) return <p className="p-8 text-destructive">{t('imports.notFound')}</p>;

  return <ImportOrderDetail order={order} />;
}

function ImportOrderDetail({ order }: { order: ImportOrderResponse }) {
  const { t } = useTranslation();
  // The STAGE decides which controls exist — a closed shipment has none of
  // them for anybody (BR-PUR-06-10) — and the PERMISSION decides whether they
  // are usable, which greys them (BR-PERM-18). Welding the two into one
  // boolean is what made this screen draw a reader nothing at all.
  const stageEditable = isEditable(order.stage);
  const canEdit = useCan('purchasing.edit') && stageEditable;

  return (
    <div className="space-y-4">
      <PurchasesTabs />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-semibold">
            <Ltr value={order.ref} className="font-mono" />
            <StageChip stage={order.stage} />
          </h1>
          <p className="text-sm text-muted-foreground">
            {order.supplierName} ·{' '}
            {order.purchaseOrderId === null || order.purchaseOrderCode === null ? (
              // Wave 5E — a DIRECT import has no purchase order behind it.
              t('imports.directImport')
            ) : (
              <Link to={`/purchases/${order.purchaseOrderId}`} className="hover:underline">
                <Ltr value={order.purchaseOrderCode} className="font-mono" />
              </Link>
            )}
          </p>
        </div>
        <StageRail order={order} stageEditable={stageEditable} />
      </div>

      {/* S4 — the record-navigation rail (wave 2). This screen has no toolbar
          primitive to carry it, so it stands on its own row. Every field here
          saves on blur, so there is never typed work for the arrows to wait on. */}
      <DocumentHeaderControls kind="importOrder" id={order.id} dirty={false} />
      <ShipmentActs order={order} />

      {order.stage === 'cancelled' && (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {t('imports.cancelledNotice', { reason: order.cancelReason ?? '' })}
        </p>
      )}

      <ShipmentCard order={order} canEdit={canEdit} />
      <LinesCard order={order} canEdit={canEdit} />

      <div className="grid gap-4 lg:grid-cols-2">
        <ChargesCard order={order} canEdit={canEdit} stageEditable={stageEditable} />
        <div className="space-y-4">
          <SummaryCard order={order} />
          <LandedTotalCard order={order} stageEditable={stageEditable} />
        </div>
      </div>

      {order.receiving.open && <ReceivingCard order={order} />}
      <DocumentsCard order={order} />
      {/* Wave 2 — INV-H5's files panel on the shipment: the bill of lading,
          the customs declaration. Permission follows purchasing.*. */}
      <AttachmentsPanel kind="import_order" entityId={order.id} />

      {order.stage === 'closed' && (
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          {t('imports.closedNotice')}
        </p>
      )}
    </div>
  );
}

/**
 * Wave 5E — the shipment's paper and its spreadsheet. Print/PDF is wave 2's
 * pair on the `import_order` kind (the worker re-reads the row under RLS;
 * the local print uses the figures on screen). The export is the lines AND
 * the charges, one sheet, figures as numbers so the columns sum.
 */
function ShipmentActs({ order }: { order: ImportOrderResponse }) {
  const { t, i18n } = useTranslation();
  const baseCurrency = useBaseCurrency();
  const locale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <DocumentActions
        request={{ kind: 'import_order', locale, subjectId: order.id }}
        payload={{ kind: 'import_order', data: importOrderDocument(order, baseCurrency) }}
      />
      <Button variant="outline" onClick={() => exportShipment(order, t, locale === 'ar')}>
        {t('doclist.export')}
      </Button>
    </div>
  );
}

/** One sheet: a row per line, then a row per charge — the `imports.exportRow` column says which. */
export function exportShipment(order: ImportOrderResponse, t: TFunction, rightToLeft: boolean): void {
  const headers = [
    t('imports.exportRow'),
    t('imports.colItem'),
    t('imports.colHs'),
    t('imports.colOrigin'),
    t('imports.colQty'),
    t('imports.colUnitPrice', { currency: order.currencyCode }),
    t('imports.colLineCost'),
    t('imports.colShare'),
    t('imports.colLandedUnit'),
    t('imports.colLandedUnitBase'),
    t('imports.colAmount'),
    t('imports.colCurrency'),
    t('imports.colBill'),
  ];
  const lines = order.lines.map((line) => [
    t('imports.exportLine'),
    `${line.sku} ${line.name}`,
    line.hsCode ?? '',
    line.originCountry ?? '',
    line.qty,
    line.unitPrice,
    line.lineCost,
    line.chargeShare,
    line.landedUnitCost,
    line.landedUnitCostBase,
    '',
    order.currencyCode,
    '',
  ]);
  const charges = order.charges.map((charge) => [
    t('imports.exportCharge'),
    charge.category === 'other' && charge.label ? charge.label : t(`imports.charge_${charge.category}`),
    '',
    '',
    '',
    '',
    '',
    charge.amountOrder,
    '',
    charge.amountBase,
    charge.amount,
    charge.currency,
    charge.purchaseInvoiceCode ?? '',
  ]);
  downloadXlsx(
    `${order.ref}.xlsx`,
    order.ref,
    headers,
    [...lines, ...charges],
    { numeric: [4, 5, 6, 7, 8, 9, 10] },
    rightToLeft,
  );
}

/**
 * §7's stage rail. Forward is one button; backward is a second button that
 * demands a typed reason and disappears entirely once goods have been
 * received — BOTH halves are BR-PUR-06-20, whose single sentence reads "A
 * shipment must not be moved backward without an import coordinator and a
 * typed reason, and not at all once goods have been received."
 *
 * The second half used to be attributed here to the Incoterm rule — a
 * DIFFERENT rule that lives at the `Field label={t('imports.incoterm')}`
 * call below, where it is now enforced and cited at its own line. Nothing
 * about the stage rail ever concerned Incoterms; the wrong id here was
 * counting a then-unenforced rule as covered.
 */
function StageRail({
  order,
  stageEditable,
}: {
  order: ImportOrderResponse;
  stageEditable: boolean;
}) {
  const { t } = useTranslation();
  const advance = useAdvanceStage(order.id);
  const cancel = useCancelImport(order.id);
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const forward = nextStage(order.stage);
  const back = previousStage(order.stage);
  // Wave 5E — the cancelled shipment's rail ends in its terminal stage.
  const rail = order.stage === 'cancelled' ? [...IMPORT_STAGES, 'cancelled' as const] : IMPORT_STAGES;

  return (
    <div className="space-y-2">
      <ol className="flex flex-wrap items-center gap-1 text-xs" aria-label={t('imports.stageRail')}>
        {rail.map((stage) => (
          <li
            key={stage}
            aria-current={stage === order.stage ? 'step' : undefined}
            className={cn(
              'rounded px-2 py-1',
              stageIndex(stage) < stageIndex(order.stage) && 'text-muted-foreground',
              stage === order.stage && 'bg-primary/10 font-medium text-foreground',
              // Full-strength: /50 measured 2.00:1 in light and no alpha of
              // this token clears 4.5 — order and aria-current already say
              // which stages are ahead.
              stageIndex(stage) > stageIndex(order.stage) && 'text-muted-foreground',
            )}
          >
            {t(`imports.stage_${stage}`)}
          </li>
        ))}
      </ol>
      {stageEditable && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {back && canGoBack(order.stage) && !reversing && (
            <Gated permission="purchasing.edit" reasonKey="perm.cannot.moveImports">
              <Button variant="ghost" size="sm" onClick={() => setReversing(true)}>
                {t('imports.goBack')}
              </Button>
            </Gated>
          )}
          {/* Wave 5E — Cancel, from any stage before goods are received.
              A commitment to a supplier ends, so it is the APPROVE action
              and asks GEN-59's typed reason in §8's ReasonBox. */}
          {canCancel(order) && (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.cancelImports">
              <Button variant="outline" size="sm" onClick={() => setCancelAsk(true)}>
                {t('imports.cancelShipment')}
              </Button>
            </Gated>
          )}
          {forward && (
            <Gated permission="purchasing.edit" reasonKey="perm.cannot.moveImports">
              <Button
                size="sm"
                disabled={advance.isPending}
                onClick={() => advance.mutate({ stage: forward })}
              >
                {t('imports.advanceTo', { stage: t(`imports.stage_${forward}`) })}
              </Button>
            </Gated>
          )}
        </div>
      )}
      {canCancel(order) ? (
        <ReasonBox
          open={cancelAsk}
          title={t('imports.confirmCancel')}
          prompt={t('imports.confirmCancelBody')}
          reasonLabel={t('gen72.cancelReasonLabel')}
          confirmLabel={t('gen72.confirmCancel')}
          busy={cancel.isPending}
          onConfirm={(typed) => {
            setCancelAsk(false);
            cancel.mutate(typed);
          }}
          onCancel={() => setCancelAsk(false)}
        />
      ) : null}
      {cancel.isError && (
        <p role="alert" className="text-end text-sm text-destructive">{(cancel.error as Error).message}</p>
      )}
      {reversing && back && (
        <div className="flex flex-wrap items-end justify-end gap-2">
          <div>
            <Label htmlFor="reversal-reason">{t('imports.reversalReason')}</Label>
            <Input
              id="reversal-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="w-72"
            />
          </div>
          <Button
            size="sm"
            variant="destructive"
            disabled={reason.trim().length === 0 || advance.isPending}
            onClick={() => {
              advance.mutate(
                { stage: back, reason: reason.trim() },
                { onSuccess: () => { setReversing(false); setReason(''); } },
              );
            }}
          >
            {t('imports.confirmReversal')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setReversing(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      )}
      {advance.isError && (
        <p className="text-end text-sm text-destructive">{(advance.error as Error).message}</p>
      )}
    </div>
  );
}

/** §5b — the shipment's own facts, plus the FX rate every base figure
    divides by. EC-19-19 — the rate is ALWAYS VISIBLE, with its policy named
    beside it: a converted amount must always be shown with the rate that
    produced it, to everybody. */
function ShipmentCard({ order, canEdit }: { order: ImportOrderResponse; canEdit: boolean }) {
  const { t } = useTranslation();
  const update = useUpdateImportOrder(order.id);
  // The tenant's base (GEN-107) — what BR-PUR-06-15's "(USD)" actually names.
  // This NAMES the company's money; the order's own figures keep reading the
  // order (GEN-108), which is why nothing else on this screen uses the hook.
  const baseCurrency = useBaseCurrency();
  const [draft, setDraft] = useState({
    orderDate: order.orderDate,
    shipDate: order.shipDate,
    arriveDate: order.arriveDate,
    carrier: order.carrier ?? '',
    trackingNo: order.trackingNo ?? '',
    manualRef: order.manualRef ?? '',
    notes: order.notes ?? '',
  });
  // Wave 5E — the order date moves only while the shipment is a draft (and
  // within its reference's year, which the server checks).
  const canEditOrderDate = canEdit && order.stage === 'draft';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('imports.shipment')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* A three-letter Incoterm and a rate expression are codes; the ship
            method and the warehouse name are words in the reader's language.

            BR-PUR-06-21 — "The Incoterm codes must be shown with their
            meanings, not as bare three-letter codes." The code stays the
            value (it is what the supplier's paperwork says) and the meaning
            sits under it as the hint, in the reader's language — the same
            split G-INCOTERMS itself makes between the code and the plain
            words. ERR-PUR-06-09 is the §14 row this closes: "Wondered what
            FOB means | nothing — the code is shown bare" stops being
            reachable because no code on this screen is bare any more. The
            picker on import-new-page.tsx carries the same six meanings. */}
        <Field
          label={t('imports.incoterm')}
          value={order.incoterm}
          code
          hint={t(`imports.incoterm_${order.incoterm}`)}
        />
        <Field label={t('imports.shipMethod')} value={t(`imports.method_${order.shipMethod}`)} />
        <Field label={t('imports.warehouse')} value={order.warehouseName} />
        {/* BR-PUR-06-15 — "When the order currency is the base currency, no
            rate box is shown and the line 'Order is in base currency (USD)'
            appears instead. The prototype already does this correctly." The
            comparison is against the TENANT's base (GEN-107, useBaseCurrency)
            rather than a 'USD' literal, so the sentence stays true for a
            company that reports in something else — with a USD base it is the
            spec's line byte for byte. A base-currency shipment divides by
            nothing, so a box reading "1 USD = 1" would be a rate that never
            participates — the prototype knew not to show one.

            The `hint` on the rate box displays a stored policy. BR-PUR-06-16 — rule-coverage:ignore
            — asked that policy to be AUTHORITATIVE, and displaying is
            not obeying: no landed-cost calculation reads `ratePolicy` (see
            imports.service.ts, which says so in its own words at `receive`),
            so the column decides nothing. Delegated ruling D56 (30 Aug 2026)
            settled "order date or payment date": the payment-date arm is
            retired reversibly (undefined before a payment exists), the
            landed cost's answer is the order's frozen rate, and the rule
            leaves the queue by a notice at its spec declaration
            (finalnotices--spec.patch) rather than by a citation here — the
            hint still only displays, so the mark stays. Reversal: a real
            payment-date revaluation design landing. */}
        {order.currencyCode === baseCurrency ? (
          <Field label={t('imports.currency')} value={t('imports.baseCurrencyLine', { currency: baseCurrency })} />
        ) : (
          <Field
            label={t('imports.rate')}
            // Wave 1C: the stored 6-dp rate is brought to the four decimals
            // it was typed at before the exact inversion (see `displayRate`).
            value={`1 ${order.currencyCode} = ${inverseRate(displayRate(order.rate)) ?? '—'}`}
            hint={t(`imports.ratePolicy_${order.ratePolicy}`)}
            code
          />
        )}
        <div>
          <Label htmlFor="order-date">{t('imports.orderDate')}</Label>
          <Input
            id="order-date"
            type="date"
            value={draft.orderDate}
            disabled={!canEditOrderDate}
            onChange={(event) => setDraft({ ...draft, orderDate: event.target.value })}
            onBlur={() =>
              canEditOrderDate &&
              draft.orderDate !== order.orderDate &&
              update.mutate({ orderDate: draft.orderDate })
            }
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('imports.orderDateHint')}</p>
        </div>
        <div>
          <Label htmlFor="ship-date">{t('imports.shipDate')}</Label>
          <Input
            id="ship-date"
            type="date"
            value={draft.shipDate}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, shipDate: event.target.value })}
            onBlur={() => canEdit && update.mutate({ shipDate: draft.shipDate })}
          />
        </div>
        <div>
          <Label htmlFor="arrive-date">{t('imports.arriveDate')}</Label>
          <Input
            id="arrive-date"
            type="date"
            value={draft.arriveDate}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, arriveDate: event.target.value })}
            onBlur={() => canEdit && update.mutate({ arriveDate: draft.arriveDate })}
          />
        </div>
        <div>
          <Label htmlFor="carrier">{t('imports.carrier')}</Label>
          <Input
            id="carrier"
            value={draft.carrier}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, carrier: event.target.value })}
            onBlur={() => canEdit && update.mutate({ carrier: draft.carrier })}
          />
        </div>
        <div>
          <Label htmlFor="tracking">{t('imports.tracking')}</Label>
          <Input
            id="tracking"
            value={draft.trackingNo}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, trackingNo: event.target.value })}
            onBlur={() => canEdit && update.mutate({ trackingNo: draft.trackingNo })}
          />
        </div>
        <div>
          <Label htmlFor="manual-ref">{t('imports.manualRef')}</Label>
          <Input
            id="manual-ref"
            value={draft.manualRef}
            maxLength={60}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, manualRef: event.target.value })}
            onBlur={() =>
              canEdit &&
              draft.manualRef !== (order.manualRef ?? '') &&
              update.mutate({ manualRef: draft.manualRef.trim() === '' ? null : draft.manualRef })
            }
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Label htmlFor="import-notes">{t('imports.notes')}</Label>
          <Textarea
            id="import-notes"
            value={draft.notes}
            maxLength={2000}
            disabled={!canEdit}
            onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            onBlur={() =>
              canEdit &&
              draft.notes !== (order.notes ?? '') &&
              update.mutate({ notes: draft.notes.trim() === '' ? null : draft.notes })
            }
          />
        </div>
        {update.isError && (
          <p className="sm:col-span-2 lg:col-span-4 text-sm text-destructive">
            {(update.error as Error).message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One labelled read-only value — and `code` is a BIDI decision, not styling.
 *
 * `code` marks a value that is LEFT-TO-RIGHT BY NATURE: an incoterm, a rate
 * expression, a reference. Those get `<Ltr>` — `<bdi dir="ltr">` — which is
 * exactly what `amount.tsx` documents it for ("Dates and codes are LTR islands
 * too"), and the rate string is the case it describes: `1 EUR = 1.0800` is one
 * run of figures and neutrals that must share a single island.
 *
 * Everything else here is NATURAL LANGUAGE — a translated word, a warehouse
 * somebody named — and its direction belongs to its CONTENT. Those get a bare
 * `<bdi>`, whose default `dir="auto"` takes the base direction from the first
 * strong character: LTR for "Main", RTL for "بحري". The isolation is kept,
 * because a value may run opposite to the page around it; only the FORCED
 * direction goes.
 *
 * ── WHAT THIS FIXES ───────────────────────────────────────────────────────
 *
 * Every value went through `<Ltr>`, so `t('imports.method_sea')` ("بحري") and
 * an Arabic warehouse name were both rendered inside `dir="ltr"`. Forcing an
 * Arabic value's base direction to LTR is not cosmetic: the neutrals in it —
 * spaces, hyphens, any Latin or numeric token — resolve against the wrong
 * paragraph direction, so a name like "مستودع جدة 2" reorders.
 *
 * It is a DIFFERENT defect from the "≈" one this screen already carries a
 * guard for. That one was a neutral left outside an island; this one is an
 * island imposed where none was wanted — the opposite error, on the same
 * screen, which is why finding the first did not surface the second.
 *
 * `work-order-page.tsx`'s own `Field` already gates the island behind a flag
 * and passes `warehouseName` and its translated priority through unforced. The
 * same screen family had already decided what correct looks like; this one
 * simply had not been told.
 */
function Field({
  label,
  value,
  hint,
  code,
}: {
  label: string;
  value: string;
  hint?: string;
  code?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{code === true ? <Ltr value={value} /> : <bdi>{value}</bdi>}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * §5c's lines. The last two columns are the reason the screen exists: the
 * landed unit cost in the supplier's currency and the same figure in base,
 * shown with a ≈ because it is a conversion and not a second price. Unit price
 * and landed cost sit side by side deliberately — the gap between them IS the
 * information ("$44.00 became $46.87").
 */
function LinesCard({ order, canEdit }: { order: ImportOrderResponse; canEdit: boolean }) {
  const { t } = useTranslation();
  const updateLine = useUpdateImportLine(order.id);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('imports.lines')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('imports.colItem')}</TableHead>
                <TableHead>{t('imports.colHs')}</TableHead>
                <TableHead>{t('imports.colOrigin')}</TableHead>
                {/* Wave 5E — the customs duty rate and the two apportioning bases. */}
                <TableHead className="text-end">{t('imports.colDuty')}</TableHead>
                <TableHead className="text-end">{t('imports.colWeight')}</TableHead>
                <TableHead className="text-end">{t('imports.colVolume')}</TableHead>
                <TableHead className="text-end">{t('imports.colQty')}</TableHead>
                <TableHead className="text-end">
                  {t('imports.colUnitPrice', { currency: order.currencyCode })}
                </TableHead>
                <TableHead className="text-end">{t('imports.colLineCost')}</TableHead>
                <TableHead className="text-end">{t('imports.colShare')}</TableHead>
                <TableHead className="text-end">{t('imports.colLandedUnit')}</TableHead>
                <TableHead className="text-end">{t('imports.colLandedUnitBase')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.lines.map((line) => {
                const save = (field: LineField) => (value: string | null) =>
                  updateLine.mutate({ lineId: line.id, body: { [field]: value } });
                return (
                  <TableRow key={line.id}>
                    <TableCell>
                      <p className="font-medium">{line.name}</p>
                      <Ltr value={line.sku} className="font-mono text-xs text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <LineInput
                        label={t('imports.lineFieldFor', { field: t('imports.colHs'), name: line.name })}
                        value={line.hsCode}
                        editable={canEdit}
                        code
                        onSave={save('hsCode')}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      <LineInput
                        label={t('imports.lineFieldFor', { field: t('imports.colOrigin'), name: line.name })}
                        value={line.originCountry}
                        editable={canEdit}
                        onSave={save('originCountry')}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      <LineInput
                        label={t('imports.lineFieldFor', { field: t('imports.colDuty'), name: line.name })}
                        value={line.customsDutyPct}
                        editable={canEdit}
                        numeric
                        onSave={save('customsDutyPct')}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      <LineInput
                        label={t('imports.lineFieldFor', { field: t('imports.colWeight'), name: line.name })}
                        value={line.weightKg}
                        editable={canEdit}
                        numeric
                        onSave={save('weightKg')}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      <LineInput
                        label={t('imports.lineFieldFor', { field: t('imports.colVolume'), name: line.name })}
                        value={line.volumeM3}
                        editable={canEdit}
                        numeric
                        onSave={save('volumeM3')}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      {/* N-4: the unit's decimals, and the unit beside them. */}
                      <Qty value={line.qty} precision={line.unitPrecision} unit={line.unitSymbol} />
                    </TableCell>
                    <TableCell className="text-end">
                      <Amount value={line.unitPrice} currency={order.currencyCode} />
                    </TableCell>
                    <TableCell className="text-end">
                      <Amount value={line.lineCost} currency={order.currencyCode} />
                    </TableCell>
                    <TableCell className="text-end text-muted-foreground">
                      <Amount value={line.chargeShare} currency={order.currencyCode} />
                    </TableCell>
                    <TableCell className="text-end font-medium">
                      <Amount value={line.landedUnitCost} currency={order.currencyCode} />
                    </TableCell>
                    <TableCell className="text-end text-muted-foreground">
                      {/*
                       * `dir="ltr"` on the WRAPPER, so the sign and the figure
                       * share ONE island — the guard `overview-page.tsx` puts on
                       * the identical shape, not a second approach to it. "≈" is
                       * bidi-NEUTRAL and `<Amount>` is a `<bdi>` isolate, which
                       * resolves as a neutral too; in Arabic the cell therefore
                       * holds nothing but neutrals, they take the PARAGRAPH's
                       * direction, and the sign is laid out on the far side of
                       * the number it qualifies. amount.tsx's own rule for a run
                       * of figures sharing an island, applied to the neutral
                       * that introduces one.
                       */}
                      <span dir="ltr">
                        ≈ <Amount value={line.landedUnitCostBase} />
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            {/* EC-19-20 — "the totals row on the line table is still
                required, because a table of numbers that does not add itself
                up invites somebody to add them by hand and get it wrong."
                The figures are the SERVER's summary (F-31/F-32), never a
                second client-side sum. */}
            <TableFooter>
              <TableRow>
                <TableCell colSpan={8} className="font-medium">
                  {t('imports.linesTotal')}
                </TableCell>
                <TableCell className="text-end font-medium">
                  <Amount value={order.summary.itemsCost} currency={order.currencyCode} />
                </TableCell>
                <TableCell className="text-end font-medium">
                  <Amount value={order.summary.chargesTotal} currency={order.currencyCode} />
                </TableCell>
                <TableCell colSpan={2} className="text-end font-medium">
                  <Amount value={order.summary.totalLanded} currency={order.currencyCode} />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
        {updateLine.isError && (
          <p role="alert" className="p-4 text-sm text-destructive">
            {(updateLine.error as Error).message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

type LineField = 'hsCode' | 'originCountry' | 'customsDutyPct' | 'weightKg' | 'volumeM3';

/**
 * Wave 5E — one per-line fact, saved on blur like the rest of this screen.
 * An empty box clears the field (null). Read-only it is the stored value or
 * a dash, so a reader without `purchasing.edit` sees the same table.
 */
function LineInput({
  label,
  value,
  editable,
  numeric,
  code,
  onSave,
}: {
  label: string;
  value: string | null;
  editable: boolean;
  numeric?: boolean;
  code?: boolean;
  onSave: (value: string | null) => void;
}) {
  const stored = value ?? null;
  const shown = numeric && stored !== null ? trimDecimal(stored) : stored;
  const [typed, setTyped] = useState(shown ?? '');
  useEffect(() => setTyped(shown ?? ''), [shown]);
  if (!editable) {
    return code === true || numeric === true ? <Ltr value={shown ?? '—'} className="font-mono text-xs" /> : <>{shown ?? '—'}</>;
  }
  return (
    <Input
      aria-label={label}
      inputMode={numeric ? 'decimal' : undefined}
      className={cn('h-9 w-24', numeric && 'text-end font-mono', code && 'font-mono')}
      value={typed}
      onChange={(event) => setTyped(event.target.value)}
      onBlur={() => {
        const next = typed.trim() === '' ? null : typed.trim();
        const unchanged =
          next === null || stored === null
            ? next === stored
            : numeric
              ? Number(next) === Number(stored)
              : next === stored;
        if (!unchanged) onSave(next);
      }}
    />
  );
}

/** '12.500000' → '12.5' — the 6-dp column read back at the decimals it was typed with. */
function trimDecimal(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

/**
 * §5d — the ONE place a costing figure is stored (BR-PUR-06-17). The four
 * named categories always have a row so nobody has to discover that duty is
 * enterable; extra charges are added below them and carry a description,
 * which BR-PUR-06-09 requires of any charge that is not one of the four.
 */
function ChargesCard({
  order,
  canEdit,
  stageEditable,
}: {
  order: ImportOrderResponse;
  canEdit: boolean;
  stageEditable: boolean;
}) {
  const { t } = useTranslation();
  const setCharges = useSetCharges(order.id);
  const baseCurrency = useBaseCurrency();

  const [rows, setRows] = useState(() => chargeRowsOf(order));
  const [dirty, setDirty] = useState(false);
  const orderRev = order.charges
    .map((c) => `${c.category}:${c.label ?? ''}:${c.amount}:${c.currency}:${c.exchangeRate ?? ''}:${c.purchaseInvoiceId ?? ''}`)
    .join('|');
  useEffect(() => {
    // Re-seed from the server whenever the stored charges change, including
    // after a typed total is distributed into them.
    setRows(chargeRowsOf(order));
    setDirty(false);
    // Deliberately keyed on the serialised charges rather than on `order`:
    // re-seeding on every render would discard what is being typed.
  }, [orderRev]);

  const edit = (index: number, patch: Partial<ChargeRow>) => {
    const next = [...rows];
    next[index] = { ...rows[index]!, ...patch };
    setRows(next);
    setDirty(true);
  };
  /** Wave 5E (a): a charge in a third currency needs its rate; base and the shipment's own do not. */
  const needsRate = (row: ChargeRow) => row.currency !== order.currencyCode && row.currency !== baseCurrency;

  const missingLabel = rows.some(
    (row) => row.category === 'other' && Number(row.amount) > 0 && row.label.trim() === '',
  );
  const missingRate = rows.some((row) => needsRate(row) && row.exchangeRate.trim() === '');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('imports.charges')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="alloc-method">{t('imports.allocMethod')}</Label>
          <AllocMethodSelect order={order} canEdit={canEdit} />
          <p className="mt-1 text-xs text-muted-foreground">{t('imports.allocHint')}</p>
        </div>

        {rows.map((row, index) => (
          <div key={index} className="space-y-1 border-b pb-2 last:border-b-0">
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-28 shrink-0">
                <Label className="text-xs">{t(`imports.charge_${row.category}`)}</Label>
              </div>
              {row.category === 'other' && (
                <Input
                  aria-label={t('imports.chargeLabel')}
                  placeholder={t('imports.chargeLabel')}
                  className="min-w-32 flex-1"
                  value={row.label}
                  disabled={!canEdit}
                  onChange={(event) => edit(index, { label: event.target.value })}
                />
              )}
              <Input
                aria-label={t(`imports.charge_${row.category}`)}
                inputMode="decimal"
                className="w-32 text-end font-mono"
                value={row.amount}
                disabled={!canEdit}
                onChange={(event) => edit(index, { amount: event.target.value })}
              />
              {/* Wave 5E (a) — the currency this charge was billed in. */}
              <Input
                aria-label={t('imports.chargeCurrencyFor', { charge: t(`imports.charge_${row.category}`) })}
                className="w-20 font-mono uppercase"
                maxLength={3}
                value={row.currency}
                disabled={!canEdit}
                onChange={(event) => edit(index, { currency: event.target.value.toUpperCase() })}
              />
              {needsRate(row) && (
                <Input
                  aria-label={t('imports.chargeRateFor', {
                    charge: t(`imports.charge_${row.category}`),
                    base: baseCurrency,
                  })}
                  placeholder={t('imports.chargeRate', { base: baseCurrency })}
                  inputMode="decimal"
                  className="w-32 text-end font-mono"
                  value={row.exchangeRate}
                  disabled={!canEdit}
                  onChange={(event) => edit(index, { exchangeRate: event.target.value })}
                />
              )}
            </div>
            <ChargeBillCell
              row={row}
              canEdit={canEdit}
              onChange={(bill) =>
                edit(index, { purchaseInvoiceId: bill?.id ?? null, purchaseInvoiceCode: bill?.code ?? null })
              }
            />
          </div>
        ))}

        {stageEditable && (
          <div className="flex flex-wrap gap-2">
            <Gated permission="purchasing.edit" reasonKey="perm.cannot.editImportCharges">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRows([...rows, blankCharge(order.currencyCode)]);
                  setDirty(true);
                }}
              >
                {t('imports.addCharge')}
              </Button>
            </Gated>
            <Gated permission="purchasing.edit" reasonKey="perm.cannot.editImportCharges">
            <Button
              size="sm"
              disabled={!dirty || missingLabel || missingRate || setCharges.isPending}
              onClick={() =>
                setCharges.mutate({
                  charges: rows
                    .filter((row) => Number(row.amount) !== 0 || row.category !== 'other')
                    .map((row) => ({
                      category: row.category,
                      amount: row.amount === '' ? '0.00' : row.amount,
                      ...(row.label.trim() ? { label: row.label.trim() } : {}),
                      ...(row.currency !== '' && row.currency !== order.currencyCode ? { currency: row.currency } : {}),
                      ...(needsRate(row) && row.exchangeRate.trim() !== '' ? { exchangeRate: row.exchangeRate.trim() } : {}),
                      ...(row.purchaseInvoiceId ? { purchaseInvoiceId: row.purchaseInvoiceId } : {}),
                    })),
                })
              }
            >
              {t('imports.saveCharges')}
            </Button>
            </Gated>
          </div>
        )}
        {missingLabel && <p className="text-sm text-destructive">{t('imports.needDescription')}</p>}
        {missingRate && <p className="text-sm text-destructive">{t('imports.needRate')}</p>}
        {setCharges.isError && (
          <p className="text-sm text-destructive">{(setCharges.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Wave 5E (d) — the bill a charge was invoiced on, as a chip; a picker of
 * POSTED bills to link one. The picker opens only when asked, so a card of
 * five charges is not five searches.
 */
function ChargeBillCell({
  row,
  canEdit,
  onChange,
}: {
  row: ChargeRow;
  canEdit: boolean;
  onChange: (bill: { id: string; code: string } | null) => void;
}) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  if (row.purchaseInvoiceId !== null) {
    return (
      <p className="flex items-center gap-2 text-xs">
        <span className="rounded-full bg-muted px-2 py-0.5">
          {t('imports.chargeBill')} <Ltr value={row.purchaseInvoiceCode ?? ''} className="font-mono" />
        </span>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
            {t('imports.chargeBillUnlink')}
          </Button>
        )}
      </p>
    );
  }
  if (!canEdit) return null;
  if (!picking) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
        {t('imports.chargeBillLink')}
      </Button>
    );
  }
  return <ChargeBillPicker onPick={(bill) => { setPicking(false); onChange(bill); }} />;
}

function ChargeBillPicker({ onPick }: { onPick: (bill: { id: string; code: string }) => void }) {
  const { t } = useTranslation();
  const picker = useBillPicker(null, { status: 'posted' });
  return (
    <Typeahead
      label={t('imports.chargeBillLink')}
      value={null}
      onChange={(id) => {
        const bill = id ? picker.byId.get(id) : undefined;
        if (bill) onPick({ id: bill.id, code: bill.code });
      }}
      options={picker.options}
      selected={null}
      query={picker.query}
      onQueryChange={picker.setQuery}
      emptyLabel={t('picker.noMatchingBill')}
      loading={picker.loading}
    />
  );
}

interface ChargeRow {
  category: (typeof CHARGE_CATEGORIES)[number];
  label: string;
  amount: string;
  /** Wave 5E — the charge's currency (the shipment's by default) and its frozen rate. */
  currency: string;
  exchangeRate: string;
  purchaseInvoiceId: string | null;
  purchaseInvoiceCode: string | null;
}

function blankCharge(currency: string): ChargeRow {
  return {
    category: 'other',
    label: '',
    amount: '0.00',
    currency,
    exchangeRate: '',
    purchaseInvoiceId: null,
    purchaseInvoiceCode: null,
  };
}

function rowOf(charge: ImportOrderResponse['charges'][number]): Omit<ChargeRow, 'category' | 'label' | 'amount'> {
  return {
    currency: charge.currency,
    exchangeRate: charge.exchangeRate === null ? '' : displayRate(charge.exchangeRate) ?? charge.exchangeRate,
    purchaseInvoiceId: charge.purchaseInvoiceId,
    purchaseInvoiceCode: charge.purchaseInvoiceCode,
  };
}

/** The four named categories always appear, in the documented order. */
function chargeRowsOf(order: ImportOrderResponse): ChargeRow[] {
  const named = CHARGE_CATEGORIES.filter((category) => category !== 'other').map((category) => {
    const stored = order.charges.find((charge) => charge.category === category);
    return stored
      ? { category, label: '', amount: stored.amount, ...rowOf(stored) }
      : { ...blankCharge(order.currencyCode), category, label: '' };
  });
  const extras = order.charges
    .filter((charge) => charge.category === 'other')
    .map((charge) => ({ category: 'other' as const, label: charge.label ?? '', amount: charge.amount, ...rowOf(charge) }));
  return [...named, ...extras];
}

function AllocMethodSelect({ order, canEdit }: { order: ImportOrderResponse; canEdit: boolean }) {
  const { t } = useTranslation();
  const update = useUpdateImportOrder(order.id);
  return (
    <>
      <NativeSelect
        id="alloc-method"
        value={order.allocMethod}
        disabled={!canEdit || update.isPending}
        onChange={(event) => update.mutate({ allocMethod: event.target.value as AllocMethod })}
      >
        <option value="by_value">{t('imports.byValue')}</option>
        <option value="by_quantity">{t('imports.byQuantity')}</option>
        {/* Wave 5E (b) — refused by the server while any line lacks the basis. */}
        <option value="by_weight">{t('imports.byWeight')}</option>
        <option value="by_volume">{t('imports.byVolume')}</option>
      </NativeSelect>
      {update.isError && (
        <p role="alert" className="mt-1 text-sm text-destructive">{(update.error as Error).message}</p>
      )}
    </>
  );
}

/** §5d's three-row summary, each row in both currencies. */
function SummaryCard({ order }: { order: ImportOrderResponse }) {
  const { t } = useTranslation();
  const rows = [
    ['imports.itemsCost', order.summary.itemsCost, order.summary.itemsCostBase],
    ['imports.chargesTotal', order.summary.chargesTotal, order.summary.chargesTotalBase],
    ['imports.totalLanded', order.summary.totalLanded, order.summary.totalLandedBase],
  ] as const;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('imports.summary')}</CardTitle>
      </CardHeader>
      <CardContent role="group" aria-label={t('imports.summary')} className="space-y-2">
        {rows.map(([key, amount, base], index) => (
          <div
            key={key}
            className={cn(
              'flex items-baseline justify-between gap-4',
              index === rows.length - 1 && 'border-t pt-2 font-semibold',
            )}
          >
            <span>{t(key)}</span>
            <span className="text-end">
              <Amount value={amount} currency={order.currencyCode} />
              {/* The same neutral, the same island — see LinesCard above. */}
              <span dir="ltr" className="block text-xs font-normal text-muted-foreground">
                ≈ <Amount value={base} />
              </span>
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * §5e's convenience. Ruling R1 is the whole comment: this is an INPUT, not a
 * second way to hold a cost. The number typed here is spread across the charge
 * rows on the server and comes straight back as those rows, so the moment it
 * is saved there is nothing left of it but freight, duty, insurance and
 * handling — exactly what a hand-entered breakdown would have produced.
 */
function LandedTotalCard({
  order,
  stageEditable,
}: {
  order: ImportOrderResponse;
  stageEditable: boolean;
}) {
  const { t } = useTranslation();
  const setTotal = useSetLandedTotal(order.id);
  const [total, setTotal_] = useState('');

  // A closed shipment has no landed-cost input at all: there is nothing left
  // to distribute. That is the stage's answer, not the role's.
  if (!stageEditable) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('imports.typeTotal')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">{t('imports.typeTotalHint')}</p>
        <div className="flex items-end gap-2">
          <div className="grow">
            <Label htmlFor="landed-total">
              {t('imports.totalCharges', { currency: order.currencyCode })}
            </Label>
            <Input
              id="landed-total"
              inputMode="decimal"
              className="text-end font-mono"
              value={total}
              onChange={(event) => setTotal_(event.target.value)}
            />
          </div>
          <Gated permission="purchasing.edit" reasonKey="perm.cannot.editImportCharges">
            <Button
              disabled={total.trim() === '' || setTotal.isPending}
              onClick={() => setTotal.mutate(total, { onSuccess: () => setTotal_('') })}
            >
              {t('imports.distribute')}
            </Button>
          </Gated>
        </div>
        {setTotal.isError && (
          <p className="text-sm text-destructive">{(setTotal.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * §5f — receiving, which BR-PUR-06-10 keeps hidden until customs clearance.
 * The quantity typed is the RUNNING TOTAL received for the line (ruling R7),
 * not this delivery's amount, so a second partial arrival is entered by
 * raising the number rather than by adding to it.
 *
 * BR-PUR-06-26 — rule-coverage:ignore — this mention stays uncounted: the
 * rule's real citation is at the refusal in imports.service. Its state now:
 *
 * MET: receiving is where stock and cost become accounting facts, and the R8
 * boundary holds — the server refuses a closed shipment, refuses an
 * over-receipt outright, and values the movement at the landed cost. MET
 * since this follow-up: "never by silently retyping the running total" — a
 * typed total BELOW what a line has already received used to return 200 and
 * silently change nothing; the server now refuses the whole call (409),
 * naming the line and both figures. A line left UNCHANGED in a mixed edit
 * (delta exactly zero) is still accepted and skipped — unchanged is not a
 * lowering.
 *
 * NOT MET: "Quantities already received are corrected by a further dated
 * goods received note or by cancelling one." The build still offers NEITHER
 * correction route — imports.service has no cancel, reverse or unreceive
 * method of any kind, so the received quantity remains a one-way ratchet;
 * the refusal makes it an honest one rather than a route down. Its natural
 * repair, a dated goods received note per edit (BR-PUR-06-13), stays blocked
 * by the open
 * TODO(grn-landed-cost) in imports.service: PUR-02's GRN values lines at the  todo-audit:ignore -- prose about the EXISTING marker in imports.service, not a new one
 * supplier's price and has no cost override, so generating one here would
 * post the wrong cost. That is a product decision about GRN shape — the
 * owner's, not a wave's.
 */
function ReceivingCard({ order }: { order: ImportOrderResponse }) {
  const { t } = useTranslation();
  const receive = useReceiveShipment(order.id);
  /*
   * A LOOKUP, not a picker: the lines are the order's own, so this asks for
   * the items THEY name (GEN-50's `ids` read) rather than for the catalogue.
   * It reads the item's tracking type, and a switched-off item still has to
   * answer that for a shipment already in the air.
   */
  const { data: items } = useItemsHeld(
    useMemo(() => order.lines.map((line) => line.itemId), [order.lines]),
  );
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [tracking, setTracking] = useState<Record<string, string>>({});
  // The company's today (N-16): a receipt date writes stock movements.
  const [receiptDate, setReceiptDate] = useDocumentDate();

  const offending = useMemo(
    () => overReceived(order.lines, edits, (line) => line.id),
    [order.lines, edits],
  );
  const outstanding = outstandingUnits(order.lines);
  const canReceive = useCan('purchasing.create');
  // The STAGE says whether receiving is open at all; the PERMISSION greys the
  // button. `enabled` keeps both because the QUANTITY FIELDS must be genuinely
  // inert either way (BR-PERM-21) — a person typing into a grid they cannot
  // submit has been lied to twice.
  const stageReceiving = receivingOpen(order.stage) && isEditable(order.stage);
  const enabled = canReceive && stageReceiving;

  /**
   * BR-MD-03-17 at the container door, sized to THIS delivery. Ruling R7's box
   * holds the running total, so what needs naming is the difference — the
   * units on the pallet in front of the clerk, not the ones booked in last
   * month. `expandKits: false`: this endpoint receives the line's own item.
   */
  const blockFor = (line: ImportOrderResponse['lines'][number]) => {
    const item = (items ?? []).find((candidate) => candidate.id === line.itemId);
    const delta = deltaQty(line.receivedQty, edits[line.id] ?? line.receivedQty);
    return captureBlocks(item as TrackedItem | undefined, delta, { expandKits: false })[0];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-3">
          {t('imports.receiving')}
          <ReceiptBadge state={order.receiving.state} percent={order.receiving.percent} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Wave 5C — the goods came in through goods receipts on this order:
            this card does not receive them again (the server refuses), and
            the landed cost goes onto those receipts at Received. */}
        {order.receiving.throughGoodsReceipt ? (
          <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
            {t('imports.errReceivedThroughGrn')}
          </p>
        ) : null}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('imports.colItem')}</TableHead>
                <TableHead className="text-end">{t('imports.colOrdered')}</TableHead>
                <TableHead className="text-end">{t('imports.colReceived')}</TableHead>
                <TableHead className="text-end">{t('imports.colOutstanding')}</TableHead>
                <TableHead className="text-end">{t('imports.colValuedAt')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.lines.map((line) => {
                const typed = edits[line.id] ?? line.receivedQty;
                const state = receiptState(line.qty, typed);
                const block = blockFor(line);
                return (
                  <Fragment key={line.id}>
                  <TableRow>
                    <TableCell>{line.name}</TableCell>
                    <TableCell className="text-end">
                      {/* N-4. */}
                      <Qty value={line.qty} precision={line.unitPrecision} unit={line.unitSymbol} />
                    </TableCell>
                    <TableCell className="text-end">
                      <Input
                        aria-label={t('imports.receivedFor', { name: line.name })}
                        inputMode="decimal"
                        disabled={!enabled}
                        className={cn(
                          'ms-auto w-24 text-end font-mono',
                          Number(typed) > Number(line.qty) && 'border-destructive text-destructive',
                        )}
                        value={typed}
                        onChange={(event) =>
                          setEdits({ ...edits, [line.id]: event.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell className="text-end font-mono tabular-nums text-muted-foreground">
                      {Math.max(0, Number(line.qty) - Number(typed))}
                    </TableCell>
                    <TableCell className="text-end">
                      <Amount value={line.landedUnitCostBase} />
                      {state === 'all' && (
                        <span className="ms-2 text-xs text-emerald-700 dark:text-emerald-400">{t('imports.allIn')}</span>
                      )}
                    </TableCell>
                  </TableRow>
                  {/*
                    OQ-05-17's sub-table under the line it belongs to, exactly
                    as the goods receipt and the sales grid draw it — one
                    block, saying how many identifiers are still outstanding.
                  */}
                  {block ? (
                    <TrackingRows
                      blocks={[block]}
                      text={{ [blockKey(block)]: tracking[line.id] ?? '' }}
                      onChange={(_key, value) => setTracking({ ...tracking, [line.id]: value })}
                      disabled={!enabled}
                      columns={5}
                      line={line.lineNo}
                    />
                  ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {offending && (
          <p className="text-sm text-destructive">
            {t('imports.overReceipt', {
              /*
               * N-4's DECIMALS, but not its unit. ERR-PUR-06-05 is a Settled,
               * verbatim sentence — "You ordered 200 LED Panel 40W. You cannot
               * receive 220." — and the item's NAME follows the figure, so a
               * symbol wedged between them reads as part of the name. The
               * unit is on the line the message is about, two rows up; the
               * sentence quotes the figure and lets the row carry the unit.
               */
              ordered: formatQty(offending.qty, offending.unitPrecision),
              item: offending.name,
              attempted: formatQty(
                edits[offending.id] ?? offending.receivedQty,
                offending.unitPrecision,
              ),
            })}
          </p>
        )}
        {outstanding > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('imports.outstanding', { units: outstanding })}
          </p>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="receipt-date">{t('imports.receiptDate')}</Label>
            <Input
              id="receipt-date"
              type="date"
              value={receiptDate}
              disabled={!enabled}
              onChange={(event) => setReceiptDate(event.target.value)}
            />
          </div>
          <Gated permission="purchasing.create" reasonKey="perm.cannot.receiveImports">
          <Button
            disabled={!stageReceiving || offending !== null || receive.isPending || Object.keys(edits).length === 0}
            onClick={() =>
              receive.mutate(
                {
                  receiptDate,
                  lines: Object.entries(edits).map(([lineId, receivedQty]) => {
                    const line = order.lines.find((row) => row.id === lineId);
                    const block = line ? blockFor(line) : undefined;
                    const identified = block
                      ? trackingPayload([block], { [blockKey(block)]: tracking[lineId] ?? '' })
                      : {};
                    return {
                      lineId,
                      receivedQty,
                      ...(identified.serialNos ? { serialNos: identified.serialNos } : {}),
                      ...(identified.batchNo ? { batchNo: identified.batchNo } : {}),
                    };
                  }),
                },
                {
                  onSuccess: () => {
                    setEdits({});
                    setTracking({});
                  },
                },
              )
            }
          >
            {t('imports.recordReceipt')}
          </Button>
          </Gated>
        </div>
        <p className="text-xs text-muted-foreground">{t('imports.receiveHint')}</p>
        {receive.isError && (
          <p className="text-sm text-destructive">{(receive.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** §5f: "Partial" and "All received" are never on screen together. */
function ReceiptBadge({ state, percent }: { state: 'none' | 'partial' | 'all'; percent: number }) {
  const { t } = useTranslation();
  if (state === 'none') return null;
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-0.5 text-xs font-medium',
        state === 'all'
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
      )}
    >
      {state === 'all' ? t('imports.allReceived') : t('imports.partial', { percent })}
    </span>
  );
}

/** §5g — the five named slots, so a missing bill of lading is visible as a gap. */
function DocumentsCard({ order }: { order: ImportOrderResponse }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('imports.documents')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {DOCUMENT_SLOTS.map((slot) => {
          const filed = order.documents.find((document) => document.slot === slot);
          return (
            <div
              key={slot}
              className={cn(
                'rounded-md border p-3 text-sm',
                filed ? 'border-emerald-300' : 'border-dashed text-muted-foreground',
              )}
            >
              <p className="font-medium">{t(`imports.doc_${slot}`)}</p>
              <p className="text-xs">
                {filed ? <Ltr value={filed.fileName} /> : t('imports.docMissing')}
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
